import {
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PROGRESS_PARALLAX,
	FLAG_ACTIVE
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

/**
 * ProgressParallax kernel — verbatim port of
 * src/particles/behaviour/ParticleProgressParallax.tick() (the SoA parity
 * oracle; do not "improve" the math). Writes the tx/ty target lanes the
 * integrate kernel eases toward — the same contract the reference has with
 * ParticleProperties.setTargetX/Y.
 *
 * Reference input -> contract input mapping (verified against
 * HomeTimeline._createSceneProps / resize, lines 417-419/476):
 *   sceneProps.sizes.container.w  = HomeWebGLSizes.virtualWidth
 *   sceneProps.sizes.container.h  = device.innerHeight = HomeWebGLSizes.innerHeight
 *   sceneProps.progress.ratio     = HomeWebGLFrame.progress
 *   sceneProps.progress.containerX= HomeWebGLFrame.containerX
 *
 * Reference math (ParticleProgressParallax.tick + _calculateParallaxDragOffsetX):
 *   absX = container.w * relX
 *   drag = (relX - 0.5 - (ratio - 0.5)) * power * container.w * 0.5
 *   targetX = disableTimelineX ? absX + xOffset            // xOffset raw px
 *                              : absX + containerX + drag + xOffset * container.w
 *   targetY = relY * container.h
 *
 * STAGE 6 — the followParticle branch (the spaces organic-lines swarm),
 * verbatim from the top of ParticleProgressParallax.tick():
 *
 *   if (followParticle):
 *     if (angleDeg > 360) angleDeg = 0            // never true: the angle is
 *                                                 // rolled once, never advanced
 *     rad = angleDeg * PI/180
 *     x = followed.position.x + cos(rad) * radius
 *     y = followed.position.y + sin(rad) * radius * (container.w / container.h)
 *     set({x, y})                                 // SOFT rel write
 *
 * Order note: the reference reads the followed particle's rel position
 * IN-TICK (targets are lower pool indexes, so their physics ran first);
 * here the physics kernel runs after this one, so the read sees the
 * followed lane's PREVIOUS-frame rel value — one frame of lag, invisible
 * under the followers' eases (the approved design's prev-frame-lane
 * strategy for the swarm).
 */
export function progressParallaxKernel(
	core: ParticleCore,
	range: PoolRange,
	containerW: number,
	containerH: number,
	containerX: number,
	progress: number
): void {
	const mask = core.behaviourMask;
	const flags = core.stateFlags;
	const halfW = containerW * 0.5;
	const degToRad = Math.PI / 180;
	//Ex globals.virtualYRatio — the reference reads container.w / container.h:
	const yRadiusRatio = containerW / containerH;

	for (let i = range.start; i < range.end; i++) {
		if (!(flags[i] & FLAG_ACTIVE) || !(mask[i] & BEHAVIOUR_PROGRESS_PARALLAX)) {
			continue;
		}

		//followParticle branch (see header — prev-frame rel read):
		const followed = core.followIdx[i];
		if (followed >= 0) {
			if (core.followAngleDeg[i] > 360) {
				core.followAngleDeg[i] = 0; //reference no-op wrap, kept
			}
			const rad = core.followAngleDeg[i] * degToRad;
			core.relX[i] = core.relX[followed] + Math.cos(rad) * core.followRadius[i];
			core.relY[i] = core.relY[followed] + Math.sin(rad) * core.followRadius[i] * yRadiusRatio;
		}

		const relX = core.relX[i];
		const absX = containerW * relX;

		if (mask[i] & BEHAVIOUR_DISABLE_TIMELINE_X) {
			core.tx[i] = absX + core.xOffset[i];
		} else {
			const drag = (relX - 0.5 - (progress - 0.5)) * core.parallaxPower[i] * halfW;
			core.tx[i] = absX + containerX + drag + core.xOffset[i] * containerW;
		}

		core.ty[i] = core.relY[i] * containerH;

		//The pure parallax targets (reference absX/absY getters) — later
		//kernels ADD to tx/ty; repell PUSH rebuilds from these:
		core.pTx[i] = core.tx[i];
		core.pTy[i] = core.ty[i];
	}
}
